'use client';

import { c as _c } from "react/compiler-runtime";
import * as Sentry from '@sentry/nextjs';
import { useEffect, useRef, useState } from 'react';
import { useCart, useUserSession } from '@libs/common-next';
import { type CartUI } from '@libs/common-node';
import { logger } from '@libs/core';

/**
 * Performance tracking using Sentry spans
 * Uses modern Sentry API with attributes and proper error handling
 */

export interface TrackingMetadataProps {
  itemCount?: number;
  paymentMethod?: string;
  fulfillmentMethod?: string;
  [key: string]: string | number | boolean | string[] | Record<string, unknown> | undefined;
}
export interface SentryTrackingContext {
  cart?: CartUI;
  cartId?: string;
  cartValue?: number;
  isGuest?: boolean;
}

/**
 * Track checkout funnel performance and drop-off points
 */
export function trackCheckoutStep(step: string, action: 'enter' | 'exit' | 'error', context: SentryTrackingContext = {}, metadata: TrackingMetadataProps = {}): void {
  try {
    // Create transaction for dashboard compatibility
    Sentry.startSpan({
      name: `checkout.step.${step}`,
      op: 'navigation.checkout',
      attributes: {
        'step.from': action === 'exit' ? step : metadata.previousStep as string,
        'step.to': action === 'enter' ? step : metadata.nextStep as string,
        'step.action': action,
        'step.duration_ms': Number(metadata.duration) || 0,
        timestamp: Date.now(),
        ...(context.cartId && {
          'cart.id': String(context.cartId)
        }),
        ...(context.cartValue && {
          'cart.value': Number(context.cartValue)
        }),
        ...(context.cart?.summary?.itemCount && {
          'cart.item_count': Number(context.cart?.summary?.itemCount)
        }),
        ...(String(context.cart?.fulfillmentGroups?.[0]?.fulfillmentMethod) && {
          'fulfillment.method': String(context.cart?.fulfillmentGroups)
        }),
        ...(context.isGuest !== undefined && {
          'user.is_guest': Boolean(context.isGuest)
        })
      }
    }, async span => {
      if (action === 'error') {
        span.setStatus({
          code: 2,
          message: 'Checkout step error'
        });
      }
      logger.debug(`Checkout step transition: ${step} - ${action}`);
    });
  } catch (error) {
    logger.warn('Failed to track checkout step:', error);
  }
}

/**
 * Track checkout journey completion and abandonment
 */
export function trackCheckoutJourney(action: 'started' | 'completed' | 'abandoned', context: SentryTrackingContext = {}, metadata: TrackingMetadataProps & {
  abandonment_step?: string;
  abandonment_reason?: string;
  completion_success?: boolean;
  total_duration_ms?: number;
} = {}): void {
  try {
    Sentry.startSpan({
      name: `checkout.${action}`,
      op: 'checkout.funnel.journey',
      attributes: {
        'journey.action': action,
        'journey.started': action === 'started',
        timestamp: Date.now(),
        ...(action === 'completed' && {
          'journey.completed': true
        }),
        ...(action === 'abandoned' && {
          'journey.abandoned': true
        }),
        ...(metadata.abandonment_step && {
          'journey.abandonment.step': metadata.abandonment_step
        }),
        ...(metadata.abandonment_reason && {
          'journey.abandonment.reason': metadata.abandonment_reason
        }),
        ...(metadata.total_duration_ms && {
          'journey.total_duration_ms': metadata.total_duration_ms
        }),
        ...(context.cartId && {
          'cart.id': context.cartId
        }),
        ...(context.cartValue && {
          'cart.value': context.cartValue
        }),
        ...(context.isGuest !== undefined && {
          'user.is_guest': context.isGuest
        }),
        ...(context.cart?.summary?.itemCount && {
          'cart.item_count': Number(context.cart?.summary?.itemCount)
        })
      }
    }, async span => {
      if (action === 'abandoned' || metadata.completion_success === false) {
        span.setStatus({
          code: 2,
          message: 'Checkout journey failed'
        });
      } else {
        span.setStatus({
          code: 1,
          message: 'OK'
        });
      }
      logger.debug(`Checkout journey ${action}:`, metadata);
    });
  } catch (error) {
    logger.warn('Failed to track checkout journey:', error);
  }
}

/**
 * Track checkout drop-off events
 */
export function trackCheckoutDropOff(step: string, reason: string, context: SentryTrackingContext = {}, metadata: TrackingMetadataProps = {}): void {
  try {
    Sentry.startSpan({
      name: `abandonment.${step}`,
      op: 'checkout.abandonment',
      attributes: {
        'dropoff.step': step,
        'dropoff.reason': reason,
        'dropoff.impact': context.cart?.summary?.totalAmount ?? 0 > 200 ? 'high' : 'medium',
        timestamp: Date.now(),
        ...(context.cart?.summary?.totalAmount && {
          'business.potential_revenue_loss': Number(context.cart?.summary?.totalAmount)
        }),
        ...(context.cartId && {
          'cart.id': context.cartId
        }),
        ...(context.cartValue && {
          'cart.value': context.cartValue
        }),
        ...(context.isGuest !== undefined && {
          'user.is_guest': context.isGuest
        })
      }
    }, async span => {
      span.setStatus({
        code: 2,
        message: `Checkout dropped at ${step}`
      });
      logger.warn(`Checkout dropoff at ${step}: ${reason}`, metadata);
    });
  } catch (error) {
    logger.warn('Failed to track checkout dropoff:', error);
  }
}

/**
 * Track payment processing success rates and timing
 */
export async function trackPaymentAttempt<T>(paymentMethod: string, operation: () => Promise<T>, context: SentryTrackingContext = {}, metadata: TrackingMetadataProps & {
  attempt_number?: number;
  failure_category?: string;
} = {}): Promise<T> {
  return Sentry.startSpan({
    name: `payment.${paymentMethod}.attempt`,
    op: 'payment',
    attributes: {
      'payment.method': paymentMethod,
      'payment.start_time': Date.now(),
      'payment.attempt_number': metadata.attempt_number || 1,
      timestamp: Date.now(),
      ...(context.cartId && {
        'cart.id': context.cartId
      }),
      ...(context.cartValue && {
        'payment.amount': context.cartValue
      }),
      ...(context.isGuest !== undefined && {
        'user.is_guest': context.isGuest
      })
    }
  }, async span => {
    try {
      const startTime = performance.now();
      const result = await operation();
      const duration = performance.now() - startTime;
      span.setAttribute('payment.duration_ms', duration);
      span.setAttribute('payment.success', true);
      span.setStatus({
        code: 1,
        message: 'OK'
      });
      logger.info(`Payment ${paymentMethod} succeeded in ${duration}ms`);
      return result;
    } catch (error) {
      span.setAttribute('payment.failed', true);
      span.setAttribute('payment.failure_category', metadata.failure_category || 'unknown');
      span.setAttribute('payment.error', error instanceof Error ? error.message : 'Unknown error');
      span.setStatus({
        code: 2,
        message: 'Payment failed'
      });

      // Track high-value payment failures
      if (context.cartValue && context.cartValue > 200) {
        span.setAttribute('business.high_value_failure', true);
        span.setAttribute('business.lost_revenue', context.cartValue);
      }
      logger.error(`Payment ${paymentMethod} failed:`, error);
      throw error;
    }
  });
}

/**
 * Track payment method selection and switching
 */
export function trackPaymentMethodSelection(selectedMethod: string, context: SentryTrackingContext = {}, metadata: TrackingMetadataProps & {
  previous_method?: string;
  method_switched?: boolean;
  available_methods?: string[];
} = {}): void {
  try {
    Sentry.startSpan({
      name: `payment.method.${selectedMethod}`,
      op: 'payment.method.selection',
      attributes: {
        'payment.selected_method': selectedMethod,
        'payment.method_switched': metadata.method_switched || false,
        timestamp: Date.now(),
        ...(metadata.previous_method && {
          'payment.previous_method': metadata.previous_method
        }),
        ...(metadata.available_methods && {
          'payment.available_methods': metadata.available_methods.join(',')
        }),
        ...(context.cartId && {
          'cart.id': context.cartId
        }),
        ...(context.cartValue && {
          'payment.amount': context.cartValue
        }),
        ...(context.isGuest !== undefined && {
          'user.is_guest': context.isGuest
        })
      }
    }, async span => {
      span.setStatus({
        code: 1,
        message: 'Payment method selected'
      });
      logger.debug(`Payment method selected: ${selectedMethod}`, metadata);
    });
  } catch (error) {
    logger.warn('Failed to track payment method selection:', error);
  }
}

/**
 * Track payment retry attempts
 */
export function trackPaymentRetry(paymentMethod: string, attemptNumber: number, context: SentryTrackingContext = {}, metadata: TrackingMetadataProps & {
  retry_reason?: string;
  original_error?: string;
} = {}): void {
  try {
    Sentry.startSpan({
      name: `${paymentMethod}.retry`,
      op: 'payment.retry',
      attributes: {
        'payment.method': paymentMethod,
        'payment.attempt_number': attemptNumber,
        'payment.retry_reason': metadata.retry_reason || 'user_initiated',
        timestamp: Date.now(),
        ...(metadata.original_error && {
          'payment.original_error': metadata.original_error
        }),
        ...(context.cartId && {
          'cart.id': context.cartId
        }),
        ...(context.cartValue && {
          'payment.amount': context.cartValue
        }),
        ...(context.isGuest !== undefined && {
          'user.is_guest': context.isGuest
        })
      }
    }, async span => {
      span.setStatus({
        code: 1,
        message: 'Payment retry initiated'
      });
      logger.debug(`Payment retry ${attemptNumber} for ${paymentMethod}:`, metadata);
    });
  } catch (error) {
    logger.warn('Failed to track payment retry:', error);
  }
}

/**
 * Monitor inventory availability impact on conversions
 */
export async function trackInventoryValidation<T>(operation: () => Promise<T>, context: SentryTrackingContext = {}, metadata: TrackingMetadataProps & {
  validation_type?: 'cart' | 'checkout' | 'realtime';
  items_validated?: number;
} = {}): Promise<T> {
  return Sentry.startSpan({
    name: 'inventory.cart_validation',
    op: 'inventory.cart_validation',
    attributes: {
      'validation.start_time': Date.now(),
      'validation.type': metadata.validation_type || 'cart',
      timestamp: Date.now(),
      ...(metadata.items_validated && {
        'inventory.items_validated': metadata.items_validated
      }),
      ...(context.cartId && {
        'cart.id': context.cartId
      }),
      ...(Number(context.cart?.summary?.itemCount) && {
        'cart.item_count': Number(context.cart?.summary?.itemCount)
      }),
      ...(String(context.cart?.fulfillmentGroups?.[0]?.fulfillmentMethod) && {
        'fulfillment.method': String(context.cart?.fulfillmentGroups)
      })
    }
  }, async span => {
    try {
      const startTime = performance.now();
      const result = await operation();
      const duration = performance.now() - startTime;
      span.setAttribute('validation.duration_ms', duration);
      span.setAttribute('validation.success', true);
      span.setStatus({
        code: 1,
        message: 'OK'
      });
      if (duration > 5000) {
        span.setAttribute('performance.slow_validation', true);
      }
      return result;
    } catch (error) {
      span.setAttribute('validation.falied', true);
      span.setAttribute('validation.error', error instanceof Error ? error.message : 'Unknown error');
      span.setStatus({
        code: 2,
        message: 'Validation failed'
      });
      trackInventoryImpact(error, context, metadata);
      throw error;
    }
  });
}

/**
 * Track inventory issues impact on conversions
 */
export function trackInventoryImpact(error: unknown, context: SentryTrackingContext = {}, metadata: TrackingMetadataProps = {}): void {
  try {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const isOutOfStock = errorMessage.toLowerCase().includes('out of stock');
    const isInsufficientStock = errorMessage.toLowerCase().includes('insufficient');
    Sentry.startSpan({
      name: `inventory.error.${errorMessage}`,
      op: 'inventory.impact',
      attributes: {
        'cart.id': context.cartId,
        'inventory.impact_type': isOutOfStock ? 'out_of_stock' : isInsufficientStock ? 'insufficient_stock' : 'unknown',
        'inventory.out_of_stock': isOutOfStock,
        'inventory.error_message': errorMessage,
        timestamp: Date.now(),
        ...(context.cartValue && {
          'business.revenue_impact': context.cartValue
        }),
        ...(metadata.itemCount && {
          'inventory.out_of_stock_count': Number(metadata.itemCount)
        }),
        ...(metadata.potential_revenue_loss && {
          'business.potential_revenue_loss': Number(metadata.potential_revenue_loss)
        })
      }
    }, async span => {
      if (context.cartValue && context.cartValue > 200) {
        span.setAttribute('business.high_value_impact', true);
      }
      logger.warn(`Inventory impact for cart ${context.cartId}: ${errorMessage}`);
    });
  } catch (error) {
    logger.warn('Failed to track inventory impact:', error);
  }
}

/**
 * Measure end-to-end order completion times
 */
export async function trackOrderCompletion<T>(operation: () => Promise<T>, context: SentryTrackingContext = {}, metadata: TrackingMetadataProps = {}): Promise<T> {
  return Sentry.startSpan({
    name: 'order.completion',
    op: 'order.process',
    attributes: {
      'order.start_time': Date.now(),
      ...(context.cartId && {
        'cart.id': context.cartId
      }),
      ...(metadata.paymentMethod && {
        'payment.method': String(metadata.paymentMethod)
      }),
      ...(context.cartValue && {
        'order.value': context.cartValue
      })
    }
  }, async span => {
    try {
      const startTime = performance.now();
      const result = await operation();
      const duration = performance.now() - startTime;
      span.setAttribute('order.completion_duration_ms', duration);
      span.setAttribute('order.success', true);
      span.setStatus({
        code: 1,
        message: 'Order completed'
      });
      return result;
    } catch (error) {
      span.setAttribute('order.failed', true);
      span.setAttribute('order.error', error instanceof Error ? error.message : 'Unknown error');
      span.setStatus({
        code: 2,
        message: 'Order failed'
      });
      throw error;
    }
  });
}

/**
 * Helper function to get transaction name based on friction type
 */
function getFrictionTransactionName(frictionType: 'form_abandon' | 'multiple_errors' | 'slow_interaction' | 'rage_click'): string {
  switch (frictionType) {
    case 'form_abandon':
      return 'ux.form';
    case 'rage_click':
      return 'ux.rage_clicks';
    case 'slow_interaction':
      return 'ux.user_hesitation';
    default:
      return 'ux.friction';
  }
}

/**
 * Identify friction points in user experience
 */
export function trackUserFriction(frictionType: 'form_abandon' | 'multiple_errors' | 'slow_interaction' | 'rage_click', element: string, context: SentryTrackingContext = {}, metadata: TrackingMetadataProps & {
  duration?: number;
  errorCount?: number;
  clickCount?: number;
  friction_context?: string;
} = {}): void {
  try {
    const transactionName = getFrictionTransactionName(frictionType);
    Sentry.startSpan({
      name: transactionName,
      op: 'ux.friction',
      attributes: {
        'friction.type': frictionType,
        'friction.element': element,
        'friction.context': metadata.friction_context || 'unknown',
        'form.abandoned': frictionType === 'form_abandon',
        'recovery.successful': false,
        timestamp: Date.now(),
        ...(metadata.duration && {
          'friction.hesitation_time_ms': metadata.duration
        }),
        ...(metadata.errorCount && {
          'friction.error_count': metadata.errorCount
        }),
        ...(metadata.clickCount && {
          'friction.click_count': metadata.clickCount
        }),
        ...(context.cartId && {
          'cart.id': context.cartId
        })
      }
    }, async span => {
      if (frictionType === 'rage_click' || metadata.errorCount && metadata.errorCount > 3) {
        span.setAttribute('friction.severity', 'high');
      }
      logger.warn(`UX friction detected: ${frictionType} on ${element}`, metadata);
    });
  } catch (error) {
    logger.warn('Failed to track user friction:', error);
  }
}

/**
 *  API calls tracking
 */
export async function trackApiCall<T>(apiName: string, operation: () => Promise<T>, context: SentryTrackingContext = {}, metadata: TrackingMetadataProps = {}): Promise<T> {
  return Sentry.startSpan({
    name: `api.${apiName}`,
    op: 'api.call',
    attributes: {
      'api.name': apiName,
      'api.start_time': Date.now(),
      timestamp: Date.now(),
      ...(context.cartId && {
        'cart.id': context.cartId
      }),
      ...metadata
    }
  }, async span => {
    try {
      const startTime = performance.now();
      const result = await operation();
      const duration = performance.now() - startTime;
      span.setAttribute('api.duration_ms', duration);
      span.setAttribute('api.success', true);
      span.setStatus({
        code: 1,
        message: 'OK'
      });
      if (duration > 2000) {
        span.setAttribute('api.slow_call', true);
      }
      logger.debug(`API call ${apiName} succeeded in ${duration}ms`);
      return result;
    } catch (error) {
      span.setAttribute('api.failed', true);
      span.setAttribute('api.error', error instanceof Error ? error.message : 'Unknown error');
      span.setStatus({
        code: 2,
        message: 'API call failed'
      });
      logger.error(`API call ${apiName} failed:`, error);
      throw error;
    }
  });
}

/**
 * Hook for initializing checkout journey tracking at component/route level
 * This ensures journey tracking happens before step tracking
 */
export const useCheckoutJourneyInitialization = (cart: {
  basketId?: string;
  summary?: {
    totalAmount?: number;
  };
  fulfillmentGroups?: Array<{
    items: unknown[];
    fulfillmentMethod?: string;
  }>;
} | null | undefined, context: SentryTrackingContext = {}, metadata: TrackingMetadataProps = {}) => {
  const journeyInitializedRef = useRef<string | null>(null);
  const metadataRef = useRef(metadata);

  // Update metadata ref when it changes
  useEffect(() => {
    metadataRef.current = metadata;
  }, [metadata]);
  useEffect(() => {
    // Initialize journey tracking when component mounts and cart is available
    if (cart?.basketId && journeyInitializedRef.current !== cart.basketId) {
      journeyInitializedRef.current = cart.basketId;
      trackCheckoutJourney('started', {
        cartId: cart.basketId,
        cartValue: cart.summary?.totalAmount,
        isGuest: context.isGuest
      }, {
        itemCount: cart.fulfillmentGroups?.reduce((sum, fg) => sum + fg.items.length, 0),
        fulfillmentMethod: cart.fulfillmentGroups?.[0]?.fulfillmentMethod,
        ...metadataRef.current
      });
      logger.debug('Journey tracking initialized at component mount', {
        cartId: cart.basketId
      });
    }
  }, [cart?.basketId]);
  return {
    journeyInitialized: journeyInitializedRef.current === cart?.basketId,
    currentJourneyCartId: journeyInitializedRef.current
  };
};

/**
 * Custom hook to get Sentry tracking context
 */
export function useSentryTrackingContext() {
  const $ = _c(5);
  const {
    cart
  } = useCart() || {};
  const {
    isAuthenticated
  } = useUserSession() || {};
  const isGuest = !isAuthenticated;
  const t0 = cart || undefined;
  const t1 = cart?.basketId;
  const t2 = cart?.summary?.totalAmount;
  let t3;
  if ($[0] !== isGuest || $[1] !== t0 || $[2] !== t1 || $[3] !== t2) {
    t3 = {
      cart: t0,
      cartId: t1,
      cartValue: t2,
      isGuest
    };
    $[0] = isGuest;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

/**
 * Track address validation issues (shipping or billing)
 * Requires context to be passed from components using useSentryTrackingContext
 */
export function trackAddressIssue(addressType: 'shipping' | 'billing', issueType: 'validation_failed' | 'schema_error' | 'api_error' | 'missing_data' | 'experian_error', error: unknown, context: SentryTrackingContext, metadata: {
  validation_errors?: Record<string, string>;
  field_errors?: string[];
  address_source?: 'form' | 'api' | 'user_input' | 'saved_address';
  validation_provider?: 'experian' | 'internal' | 'schema';
} = {}): void {
  try {
    const errorMessage = error instanceof Error ? error.message : String(error);
    Sentry.startSpan({
      name: `${addressType}_address.${issueType}`,
      op: 'address.validation',
      attributes: {
        'address.type': addressType,
        'address.issue_type': issueType,
        'address.error_message': errorMessage,
        'address.source': metadata.address_source || 'form',
        'address.validation_provider': metadata.validation_provider || 'internal',
        timestamp: Date.now(),
        ...(context.cartId && {
          'cart.id': context.cartId
        }),
        ...(context.cartValue && {
          'cart.value': context.cartValue
        }),
        ...(context.isGuest !== undefined && {
          'user.is_guest': context.isGuest
        }),
        ...(metadata.validation_errors && {
          'address.validation_errors': JSON.stringify(metadata.validation_errors)
        }),
        ...(metadata.field_errors && {
          'address.field_errors': metadata.field_errors.join(',')
        })
      }
    }, async span => {
      span.setStatus({
        code: 2,
        message: `${addressType} address ${issueType}`
      });
      logger.warn(`${addressType} address issue: ${issueType}`, {
        error: errorMessage,
        metadata
      });
    });
  } catch (error) {
    logger.warn(`Failed to track ${addressType} address issue:`, error);
  }
}

/**
 * Track address validation performance and success (shipping or billing)
 * Requires context to be passed from components using useSentryTrackingContext
 */
export async function trackAddressValidation<T>(addressType: 'shipping' | 'billing', operation: () => Promise<T>, context: SentryTrackingContext, metadata: {
  validation_type?: 'schema' | 'api' | 'experian' | 'internal';
  address_fields?: string[];
  validation_provider?: 'experian' | 'internal' | 'schema';
  is_edit?: boolean;
  is_new_address?: boolean;
} = {}): Promise<T> {
  return Sentry.startSpan({
    name: `${addressType}_address.validation`,
    op: 'address.validation',
    attributes: {
      'address.type': addressType,
      'address.validation_type': metadata.validation_type || 'schema',
      'address.validation_provider': metadata.validation_provider || 'internal',
      'address.start_time': Date.now(),
      'address.is_edit': metadata.is_edit || false,
      'address.is_new': metadata.is_new_address || false,
      timestamp: Date.now(),
      ...(context.cartId && {
        'cart.id': context.cartId
      }),
      ...(context.cartValue && {
        'cart.value': context.cartValue
      }),
      ...(context.isGuest !== undefined && {
        'user.is_guest': context.isGuest
      }),
      ...(metadata.address_fields && {
        'address.fields': metadata.address_fields.join(',')
      })
    }
  }, async span => {
    try {
      const startTime = performance.now();
      const result = await operation();
      const duration = performance.now() - startTime;
      span.setAttribute('address.duration_ms', duration);
      span.setAttribute('address.success', true);
      span.setStatus({
        code: 1,
        message: 'OK'
      });

      // Track slow validations
      if (duration > 3000) {
        span.setAttribute('performance.slow_validation', true);
        logger.warn(`Slow ${addressType} address validation: ${duration}ms`, {
          metadata
        });
      }
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      span.setAttribute('address.failed', true);
      span.setAttribute('address.error', errorMessage);
      span.setStatus({
        code: 2,
        message: 'Validation failed'
      });

      // Track the specific error
      trackAddressIssue(addressType, 'validation_failed', error, context, {
        validation_provider: metadata.validation_provider || 'internal'
      });
      throw error;
    }
  });
}

/**
 * Track address form interactions and user behavior
 * Requires context to be passed from components using useSentryTrackingContext
 */
export function trackAddressFormInteraction(addressType: 'shipping' | 'billing', interaction: 'form_started' | 'form_abandoned' | 'form_completed' | 'field_error' | 'field_fixed', context: SentryTrackingContext, metadata: {
  field_name?: string;
  error_type?: string;
  form_step?: string;
  time_on_form?: number;
} = {}): void {
  try {
    Sentry.startSpan({
      name: `${addressType}_address.form.${interaction}`,
      op: 'address.form_interaction',
      attributes: {
        'address.type': addressType,
        'address.interaction': interaction,
        'address.form_step': metadata.form_step || 'unknown',
        'address.time_on_form_ms': metadata.time_on_form || 0,
        timestamp: Date.now(),
        ...(context.cartId && {
          'cart.id': context.cartId
        }),
        ...(context.cartValue && {
          'cart.value': context.cartValue
        }),
        ...(context.isGuest !== undefined && {
          'user.is_guest': context.isGuest
        }),
        ...(metadata.field_name && {
          'address.field_name': metadata.field_name
        }),
        ...(metadata.error_type && {
          'address.error_type': metadata.error_type
        })
      }
    }, async span => {
      span.setStatus({
        code: 1,
        message: 'OK'
      });
      logger.debug(`${addressType} address form interaction: ${interaction}`, {
        metadata
      });
    });
  } catch (error) {
    logger.warn(`Failed to track ${addressType} address form interaction:`, error);
  }
}

/**
 * Track coupon code application (success or failure)
 */
export function trackCouponApplication(context: SentryTrackingContext = {}, metadata: TrackingMetadataProps & {
  coupon_code: string;
  success: boolean;
  failure_reason?: string;
  error_type?: string;
  discount_amount?: number;
  discount_type?: string;
  site_section?: string;
}): void {
  const {
    coupon_code,
    success
  } = metadata;
  try {
    const spanName = success ? 'coupon.application.success' : 'coupon.application.failure';
    const spanMessage = success ? `Coupon ${coupon_code} applied successfully` : `Coupon ${coupon_code} application failed: ${metadata.failure_reason || 'Unknown error'}`;
    Sentry.startSpan({
      name: spanName,
      op: 'coupon.application',
      attributes: {
        'coupon.code': coupon_code,
        ...(success && {
          'coupon.success': true
        }),
        ...(!success && {
          'coupon.failed': true
        }),
        timestamp: Date.now(),
        ...(context.cartId && {
          'cart.id': context.cartId
        }),
        ...(context.cartValue && {
          'cart.value': context.cartValue
        }),
        ...(context.isGuest !== undefined && {
          'user.is_guest': context.isGuest
        }),
        ...(metadata.discount_amount && {
          'coupon.discount_amount': metadata.discount_amount
        }),
        ...(metadata.discount_type && {
          'coupon.discount_type': metadata.discount_type
        }),
        ...(metadata.site_section && {
          'coupon.site_section': metadata.site_section
        }),
        ...(!success && metadata.failure_reason && {
          'coupon.failure_reason': metadata.failure_reason
        }),
        ...(!success && {
          'coupon.error_type': metadata.error_type || 'unknown'
        })
      }
    }, async span => {
      span.setStatus({
        code: success ? 1 : 2,
        message: spanMessage
      });
      logger.info(spanMessage, {
        cartId: context.cartId,
        discountAmount: metadata.discount_amount ?? 0,
        errorType: metadata.error_type
      });
    });
  } catch (error) {
    logger.warn(`Failed to track coupon application ${success ? 'success' : 'failure'}:`, error);
  }
}

/**
 * Track treat points application (success or failure)
 */
export function trackTreatPointsApplication(context: SentryTrackingContext = {}, metadata: TrackingMetadataProps & {
  points_amount: number;
  success: boolean;
  failure_reason?: string;
  error_type?: string;
  site_section?: string;
}): void {
  const {
    points_amount,
    success
  } = metadata;
  try {
    const spanName = success ? 'treat_points.application.success' : 'treat_points.application.failure';
    const spanMessage = success ? `Treat points ${points_amount} applied successfully` : `Treat points ${points_amount} application failed: ${metadata.failure_reason || 'Unknown error'}`;
    Sentry.startSpan({
      name: spanName,
      op: 'treat_points.application',
      attributes: {
        'treat_points.amount': points_amount,
        ...(success && {
          'treat_points.success': true
        }),
        ...(!success && {
          'treat_points.failed': true
        }),
        timestamp: Date.now(),
        ...(context.cartId && {
          'cart.id': context.cartId
        }),
        ...(context.cartValue && {
          'cart.value': context.cartValue
        }),
        ...(context.isGuest !== undefined && {
          'user.is_guest': context.isGuest
        }),
        ...(metadata.site_section && {
          'treat_points.site_section': metadata.site_section
        }),
        ...(!success && metadata.failure_reason && {
          'treat_points.failure_reason': metadata.failure_reason
        }),
        ...(!success && {
          'treat_points.error_type': metadata.error_type || 'unknown'
        })
      }
    }, async span => {
      span.setStatus({
        code: success ? 1 : 2,
        message: spanMessage
      });
      logger.info(spanMessage, {
        cartId: context.cartId,
        pointsAmount: points_amount,
        errorType: metadata.error_type
      });
    });
  } catch (error) {
    logger.warn(`Failed to track treat points application ${success ? 'success' : 'failure'}:`, error);
  }
}

/**
 * Track authentication attempts (login success and failures)
 */
export function trackAuthenticationAttempt(authMethod: 'email_password' | 'sso' | 'okta' | 'exchange_token', metadata: TrackingMetadataProps & {
  success: boolean;
  failure_reason?: string;
  error_code?: string | number;
  ip_address?: string;
  user_agent?: string;
  recaptcha_score?: number;
}, context: SentryTrackingContext = {}): void {
  try {
    const {
      success
    } = metadata;
    const spanName = success ? `auth.${authMethod}.success` : `auth.${authMethod}.failure`;
    const spanMessage = success ? `Authentication ${authMethod} succeeded` : `Authentication ${authMethod} failed: ${metadata.failure_reason || 'Unknown error'}`;
    Sentry.startSpan({
      name: spanName,
      op: 'auth.attempt',
      attributes: {
        'auth.method': authMethod,
        ...(success && {
          'auth.success': true
        }),
        ...(!success && {
          'auth.failed': true
        }),
        'auth.timestamp': Date.now(),
        ...(context.cartId && {
          'cart.id': context.cartId
        }),
        ...(context.cartValue && {
          'cart.value': context.cartValue
        }),
        ...(context.isGuest !== undefined && {
          'user.is_guest': context.isGuest
        }),
        ...(metadata.failure_reason && {
          'auth.failure_reason': metadata.failure_reason
        }),
        ...(metadata.error_code && {
          'auth.error_code': String(metadata.error_code)
        }),
        ...(metadata.ip_address && {
          'user.ip_address': metadata.ip_address
        }),
        ...(metadata.user_agent && {
          'user.agent': metadata.user_agent
        }),
        ...(metadata.recaptcha_score && {
          'auth.recaptcha_score': metadata.recaptcha_score
        })
      }
    }, async span => {
      span.setStatus({
        code: success ? 1 : 2,
        message: spanMessage
      });
      if (success) {
        logger.info(`Authentication ${authMethod} succeeded`, {
          cartId: context.cartId
        });
      } else {
        logger.warn(`Authentication ${authMethod} failed`, {
          failureReason: metadata.failure_reason,
          errorCode: metadata.error_code
        });
      }
    });
  } catch (error) {
    logger.warn(`Failed to track authentication ${metadata.success ? 'success' : 'failure'}:`, error);
  }
}

/**
 * Track authentication rate limiting events
 */
export function trackAuthenticationRateLimit(authMethod: 'email_password' | 'sso' | 'okta' | 'exchange_token', metadata: TrackingMetadataProps & {
  ip_address?: string;
  attempt_count?: number;
  lockout_duration?: number;
  error_code?: string | number;
} = {}, context: SentryTrackingContext = {}): void {
  try {
    Sentry.startSpan({
      name: `auth.${authMethod}.rate_limit`,
      op: 'auth.rate_limit',
      attributes: {
        'auth.method': authMethod,
        'auth.rate_limited': true,
        'auth.timestamp': Date.now(),
        ...(context.cartId && {
          'cart.id': context.cartId
        }),
        ...(context.cartValue && {
          'cart.value': context.cartValue
        }),
        ...(context.isGuest !== undefined && {
          'user.is_guest': context.isGuest
        }),
        ...(metadata.ip_address && {
          'user.ip_address': metadata.ip_address
        }),
        ...(metadata.attempt_count && {
          'auth.attempt_count': metadata.attempt_count
        }),
        ...(metadata.lockout_duration && {
          'auth.lockout_duration_ms': metadata.lockout_duration
        }),
        ...(metadata.error_code && {
          'auth.error_code': String(metadata.error_code)
        })
      }
    }, async span => {
      span.setStatus({
        code: 2,
        message: 'Authentication rate limited'
      });
      logger.warn(`Authentication rate limited for ${authMethod}`, {
        ipAddress: metadata.ip_address,
        attemptCount: metadata.attempt_count,
        lockoutDuration: metadata.lockout_duration
      });
    });
  } catch (error) {
    logger.warn('Failed to track authentication rate limit:', error);
  }
}

/**
 * Track recaptcha validation during authentication
 */
export function trackAuthenticationRecaptcha(metadata: TrackingMetadataProps & {
  success: boolean;
  ip_address?: string;
  recaptcha_score?: number;
  recaptcha_action?: string;
  failure_reason?: string;
}, context: SentryTrackingContext = {}): void {
  try {
    const {
      success
    } = metadata;
    const spanName = success ? 'auth.recaptcha.success' : 'auth.recaptcha.failure';
    const spanMessage = success ? 'Recaptcha validation succeeded' : `Recaptcha validation failed: ${metadata.failure_reason || 'Unknown error'}`;
    Sentry.startSpan({
      name: spanName,
      op: 'auth.recaptcha',
      attributes: {
        ...(success && {
          'auth.recaptcha.success': true
        }),
        ...(!success && {
          'auth.recaptcha.failed': true
        }),
        'auth.recaptcha.timestamp': Date.now(),
        ...(context.cartId && {
          'cart.id': context.cartId
        }),
        ...(context.cartValue && {
          'cart.value': context.cartValue
        }),
        ...(context.isGuest !== undefined && {
          'user.is_guest': context.isGuest
        }),
        ...(metadata.ip_address && {
          'user.ip_address': metadata.ip_address
        }),
        ...(metadata.recaptcha_score && {
          'auth.recaptcha_score': metadata.recaptcha_score
        }),
        ...(metadata.recaptcha_action && {
          'auth.recaptcha_action': metadata.recaptcha_action
        }),
        ...(metadata.failure_reason && {
          'auth.recaptcha.failure_reason': metadata.failure_reason
        })
      }
    }, async span => {
      span.setStatus({
        code: success ? 1 : 2,
        message: spanMessage
      });
      if (success) {
        logger.info('Recaptcha validation succeeded', {
          score: metadata.recaptcha_score
        });
      } else {
        logger.warn('Recaptcha validation failed', {
          score: metadata.recaptcha_score,
          failureReason: metadata.failure_reason
        });
      }
    });
  } catch (error) {
    logger.warn(`Failed to track recaptcha ${metadata.success ? 'success' : 'failure'}:`, error);
  }
}

/**
 * React Hook for automatic checkout flow tracking with duration measurement
 */
export const useCheckoutTracking = (context, currentStep, journeyInitialized) => {
  const $ = _c(11);
  const [previousStep, setPreviousStep] = useState(null);
  const [stepStartTime, setStepStartTime] = useState(null);
  let t0;
  let t1;
  if ($[0] !== context || $[1] !== currentStep || $[2] !== journeyInitialized || $[3] !== previousStep || $[4] !== stepStartTime) {
    t0 = () => {
      if (journeyInitialized === false) {
        return;
      }
      const now = performance.now();
      if (previousStep && previousStep !== currentStep) {
        const duration = stepStartTime ? now - stepStartTime : 0;
        trackCheckoutStep(previousStep, "exit", context, {
          duration,
          nextStep: currentStep
        });
        trackCheckoutStep(currentStep, "enter", context, {
          previousStep
        });
        setStepStartTime(now);
      } else {
        if (!previousStep && currentStep) {
          setStepStartTime(now);
          trackCheckoutStep(currentStep, "enter", context, {});
        }
      }
      setPreviousStep(currentStep);
    };
    t1 = [currentStep, context, previousStep, stepStartTime, journeyInitialized];
    $[0] = context;
    $[1] = currentStep;
    $[2] = journeyInitialized;
    $[3] = previousStep;
    $[4] = stepStartTime;
    $[5] = t0;
    $[6] = t1;
  } else {
    t0 = $[5];
    t1 = $[6];
  }
  useEffect(t0, t1);
  let t2;
  if ($[7] !== context || $[8] !== currentStep || $[9] !== stepStartTime) {
    t2 = {
      trackStepError: error => {
        const now_0 = performance.now();
        const duration_0 = stepStartTime ? now_0 - stepStartTime : 0;
        trackCheckoutStep(currentStep, "error", context, {
          duration: duration_0,
          error: error instanceof Error ? error.message : String(error)
        });
      },
      trackStepDuration: customDuration => {
        const now_1 = performance.now();
        const duration_1 = customDuration || (stepStartTime ? now_1 - stepStartTime : 0);
        trackCheckoutStep(currentStep, "exit", context, {
          duration: duration_1
        });
      }
    };
    $[7] = context;
    $[8] = currentStep;
    $[9] = stepStartTime;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
};